import { Icon } from '@iconify/react'
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { TypographyXSmallMuted } from '@/components/ui/typography.jsx'
import { MultiSelectCommand } from '@/pages/dashboard/library/creative/MultiSelectCommand.jsx'
import { useEffect, useState } from 'react'
import { ApproveDeleteDialog } from '@/components/dialogs/ApproveDeleteDialog.jsx'
import { motion } from 'framer-motion'
import { AnalyticsService } from '@/services/analytics.js'
import { UserRoles } from '@/consts/index.js'
import { RoleGuard } from '@/guards/RoleGuard.jsx'
import { useRoleGuard } from '@/hooks/useRoleGuard.jsx'

const generateRandomId = (length = 8) => {
    let id = ''
    const characters = 'abcdefghijklmnopqrstuvwxyz0123456789'
    for (let i = 0; i < length; i++) {
        id += characters.charAt(Math.floor(Math.random() * characters.length))
    }
    return id
}

export const CreativeProperties = ({ initialProperties = [], onPropertyChanged }) => {

    const [properties, setProperties] = useState([])
    const [propertyToDelete, setPropertyToDelete] = useState(null)
    const userAllowToEdit = useRoleGuard(UserRoles.EDITOR)


    const addNewProperty = () => {
        setProperties([
            ...properties,
            { id: generateRandomId(), label: 'New Property', tags: [] }
        ])
        AnalyticsService.track('new-property-added')
    }

    const deleteProperty = () => {
        if (!propertyToDelete) {
            return
        }
        setProperties(properties.filter(p => p.id !== propertyToDelete.id))
        setPropertyToDelete(null)
        AnalyticsService.track('property-deleted')
    }

    const updatePropertyTags = (property, newTags) => {
        setProperties(properties.map(p => p.id === property.id ? { ...p, tags: newTags } : p))
        AnalyticsService.track('property-tags-updated', { property, newTags })
    }

    useEffect(() => {
        const handler = setTimeout(() => {
            if (JSON.stringify(initialProperties) !== JSON.stringify(properties)) {
                onPropertyChanged({ properties })
            }
        }, 800)
        return () => clearTimeout(handler)
    }, [properties])

    useEffect(() => {
        if (initialProperties.length > 0) {
            setProperties(initialProperties)
        }
    }, [initialProperties])


    return (
        <div className="">

            <ApproveDeleteDialog
                isDialogOpen={!!propertyToDelete}
                onClose={() => setPropertyToDelete(null)}
                approveDelete={deleteProperty}
            />

            <div className="flex flex-col gap-1">
                {
                    properties.map((property, i) => {
                        return (
                            <div className="flex relative group" key={property.id}>

                                <RoleGuard minimumRoleRequired={UserRoles.EDITOR} hideContent>
                                    <Icon
                                        icon="ph:trash"
                                        className="h-4 w-4 shrink-0 absolute -left-5 top-2.5 text-error cursor-pointer opacity-0 transition group-hover:opacity-100"
                                        onClick={() => setPropertyToDelete(property)}
                                    />
                                </RoleGuard>

                                <motion.div
                                    className="flex items-center h-fit relative"
                                    initial={{ opacity: 0, left: "-30px" }}
                                    animate={{ opacity: 1, left: 0 }}
                                    transition={{ duration: 0.2, delay: i * 0.02 }}
                                >
                                    <TypographyXSmallMuted>
                                        {i + 1}.
                                    </TypographyXSmallMuted>
                                    <Input
                                        placeholder="Property Name"
                                        value={property.label}
                                        onChange={(e) => {
                                            setProperties(properties.map(p => p.id === property.id ? { ...p, label: e.target.value } : p))
                                        }}
                                        className="bg-transparent border-none focus-visible:ring-0 text-xsm flex-1 shadow-none w-[110px] truncate p-2"
                                    />
                                </motion.div>

                                <motion.div
                                    className="flex items-center h-fit relative"
                                    initial={{ opacity: 0, left: "-30px" }}
                                    animate={{ opacity: 1, left: 0 }}
                                    transition={{ duration: 0.2, delay: i * 0.02 }}
                                >
                                    <MultiSelectCommand
                                        disabled={!userAllowToEdit}
                                        onSelectedChange={(newSelectedTags) => updatePropertyTags(property, newSelectedTags)}
                                        initialSelected={property.tags}
                                    />
                                </motion.div>
                            </div>
                        )
                    })
                }

            </div>
            <RoleGuard minimumRoleRequired={UserRoles.EDITOR}>
                <Button className="gap-1 mt-4 p-0 hover:bg-background hover:underline" size="mini" variant="primary-ghost" onClick={addNewProperty}>
                    <Icon icon="mage:plus" className="h-4 w-4" />
                    Add A Property
                </Button>
            </RoleGuard>
        </div>
    )
}

